/**
 * RatingPage — `/rating` (design: "Reyting" artboard)
 *
 * Period chips (Hafta/Oy/Yil) · podium top-3 · "my position" highlight ·
 * full ranked list. Data from GET /api/leaderboard/.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getGlobalLeaderboard } from '@/api/social'
import { useAuth } from '@/context/AuthContext'
import { Avatar, SectionHeader } from '@/components/kit'
import { Trophy } from 'lucide-react'

const PODIUM_COLORS = ['var(--mint)', 'var(--coral)', 'var(--lav)']

export default function RatingPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [period, setPeriod] = useState('week')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  // requestIdRef guards the actual setData/setLoading calls (not just an
  // AbortController) — on fast/local connections a stale request can still
  // *complete* before its abort() takes effect, so the id check is what
  // actually stops an older period's response from overwriting a newer one.
  const requestIdRef = useRef(0)

  useEffect(() => {
    const reqId = ++requestIdRef.current
    const controller = new AbortController()
    setLoading(true)
    getGlobalLeaderboard({ period }, { signal: controller.signal })
      .then(({ data }) => { if (reqId === requestIdRef.current) setData(data) })
      .catch(() => {})
      .finally(() => { if (reqId === requestIdRef.current) setLoading(false) })
    return () => controller.abort()
  }, [period])

  const entries = data?.entries ?? []
  const top3 = entries.slice(0, 3)
  // design order: #2 left, #1 center, #3 right
  const podium = [top3[1], top3[0], top3[2]].filter(Boolean)
  const rest = entries.slice(3)
  const me = data?.me

  return (
    <div>
      <SectionHeader
        title={t('rating.title', 'Reyting')}
        subtitle={t('rating.subtitle', "Yig'ilgan ball bo'yicha umumiy reyting")}
        right={
          <div className="row gap-8">
            {[
              { k: 'week', l: t('rating.week', 'Hafta') },
              { k: 'month', l: t('rating.month', 'Oy') },
              { k: 'year', l: t('rating.year', 'Yil') },
              { k: 'all', l: t('rating.allTime', 'Umumiy') },
            ].map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)}
                className={'btn ' + (period === p.k ? 'btn-soft' : 'btn-outline')}>
                {p.l}
              </button>
            ))}
          </div>
        }
      />

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }} />
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div className="card" style={{ padding: 32, borderRadius: 22, textAlign: 'center', borderStyle: 'dashed', boxShadow: 'none' }}>
          <Trophy size={36} strokeWidth={1.5} style={{ margin: '0 auto 10px', color: 'var(--muted)' }} />
          <div className="t-sm t-mut">{t('rating.empty', "Bu davrda natijalar yo'q.")}</div>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <>
          {/* Podium */}
          <div className="card" style={{
            padding: 28, borderRadius: 28,
            background: 'linear-gradient(180deg, var(--coral-tint) 0%, var(--surface) 100%)',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${podium.length},1fr)`, gap: 24, alignItems: 'end' }}>
              {podium.map((p) => {
                const first = p.rank === 1
                const c = PODIUM_COLORS[(p.rank - 1) % 3]
                const h = first ? 160 : p.rank === 2 ? 120 : 96
                return (
                  <div key={p.student_id} style={{ textAlign: 'center' }}>
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: 12 }}>
                      <Avatar name={p.full_name} size={first ? 96 : 72} idx={p.rank + 2} />
                      <div style={{
                        position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)',
                        background: c, color: '#fff',
                        width: 32, height: 32, borderRadius: 12, display: 'grid', placeItems: 'center',
                        fontWeight: 800, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,.15)',
                      }}>#{p.rank}</div>
                    </div>
                    <div className="h-display" style={{ fontSize: first ? 18 : 16 }}>{p.full_name}</div>
                    <div className="t-sm fw-7" style={{ color: c, marginTop: 2 }}>
                      {p.total_score} {t('rating.points', 'ball')}
                    </div>
                    <div style={{ marginTop: 12, height: h, background: c, opacity: 0.18, borderRadius: '14px 14px 0 0', position: 'relative' }}>
                      {first && (
                        <div style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)' }}>
                          <Trophy size={28} color="var(--butter)" />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* My position highlight */}
          {me && (
            <div className="card" style={{
              marginTop: 16, padding: 18, borderRadius: 22,
              background: 'var(--coral)', color: '#fff', border: 0,
              display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
            }}>
              <div className="h-display" style={{ fontSize: 28, width: 60 }}>#{me.rank}</div>
              <Avatar name={me.full_name} idx={0} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fw-7">{me.full_name} ({t('rating.you', 'siz')})</div>
                <div className="t-xs" style={{ opacity: .8 }}>
                  {me.exams_count} {t('rating.examsCount', 'imtihon')} · {me.total_score} {t('rating.points', 'ball')}
                </div>
              </div>
            </div>
          )}

          {/* Full list */}
          {rest.length > 0 && (
            <div className="card" style={{ marginTop: 16, padding: 8, borderRadius: 22 }}>
              {rest.map((p, i) => {
                const isMe = p.student_id === user?.id
                return (
                  <div key={p.student_id} className="row gap-16" style={{
                    padding: '12px 16px',
                    borderTop: i ? '1px solid var(--border)' : 0,
                    background: isMe ? 'var(--coral-tint)' : 'transparent',
                    borderRadius: isMe ? 14 : 0,
                  }}>
                    <span className="fw-7 t-sm" style={{ width: 32, color: 'var(--ink-3)' }}>#{p.rank}</span>
                    <Avatar name={p.full_name} idx={p.rank} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="fw-7 t-sm">
                        {p.full_name} {isMe && <span style={{ color: 'var(--coral)' }}>({t('rating.you', 'siz')})</span>}
                      </div>
                      <div className="t-xs t-mut">{p.exams_count} {t('rating.examsCount', 'imtihon')}</div>
                    </div>
                    <div className="h-display" style={{ width: 80, textAlign: 'right' }}>{p.total_score}</div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
